import { useState, useMemo, useEffect, useRef } from "react";
import { Box, Flex } from "@chakra-ui/react";
import getNativeElementColor from "src/styling/getNativeElementColor";
import time from "src/utils/time";
import { H3, Subtitle } from "src/atoms/typography";
import TextField from "src/components/TextField";

export default function EditSchedule({
  t,
  onSubmit,
  schedule,
  scheduler,
  onTaskChange
}) {
  const [taskManager, setTaskManager] = useState({
    task: schedule || {
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      startTime: { hour: 8, minute: 0 },
      endTime: { hour: 18, minute: 0 }
    },
    updateTask: (update) => {
      setTaskManager((prevState) => ({
        ...prevState,
        task: { ...prevState.task, ...update }
      }));
    }
  });
  const initialTask = useRef(taskManager.task);
  const { task, updateTask } = taskManager;

  useEffect(() => {
    if (
      JSON.stringify(initialTask.current || {}) !==
      JSON.stringify(taskManager.task || {})
    ) {
      onTaskChange(true);
    } else onTaskChange(false);
  }, [taskManager.task, onTaskChange]);

  const daysAdjective = useMemo(
    () =>
      scheduler.getDaysAdjective({ days: task.days, t, addSeparation: true }),
    [scheduler, task.days, t]
  );

  const localizedWeekDays = scheduler.localizedWeekDays.map((day) =>
    day.slice(0, 2)
  );

  function toggleDay(dayKey) {
    if (task.days.includes(dayKey) && task.days.length !== 1) {
      updateTask({ days: task.days.filter((i) => i !== dayKey) });
    } else if (!task.days.includes(dayKey)) {
      const days = [...task.days, dayKey].sort((a, b) => {
        const dayA = scheduler.getDayIndex(a);
        const dayB = scheduler.getDayIndex(b);
        return dayA - dayB;
      });
      updateTask({ days });
    }
  }

  function splitTime(time) {
    const [hour, minute] = time.split(":");
    return { hour, minute };
  }

  function updateTime(time, type) {
    let { hour, minute } = splitTime(time);
    if (!hour) hour = 0;
    if (!minute) minute = 0;

    hour = Number(hour);
    minute = Number(minute);

    const checks = {
      endHour: type === "startTime" ? task.endTime.hour : task.startTime.hour,
      endMinute:
        type === "startTime" ? task.endTime.minute : task.startTime.minute
    };

    const overlaps = hour === checks.endHour && minute === checks.endMinute;

    if (overlaps) {
      let newHour = hour;
      let newMin = minute;
      if (newMin === 0) {
        newMin = 59;
        if (newHour === 0) newHour = 23;
        else newHour -= 1;
      } else {
        newMin -= 1;
      }

      updateTask({
        endTime: { hour: Number(newHour), minute: Number(newMin) }
      });
    }

    if (!overlaps || (overlaps && type === "startTime")) {
      updateTask({ [type]: { hour: Number(hour), minute: Number(minute) } });
    }
  }
  const startDateObj = time().set({
    hour: task.startTime.hour,
    minute: task.startTime.minute
  });
  const endDateObj = time().set({
    hour: task.endTime.hour,
    minute: task.endTime.minute
  });

  const isOverNight =
    scheduler.getMilliseconds(task.startTime) >
    scheduler.getMilliseconds(task.endTime);
  return (
    <Box
      as="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(task);
      }}
      id="scheduler.creator"
    >
      <H3>{t("scheduler.time.heading")}</H3>
      <Box
        mt="20px"
        mb="28px"
        sx={{
          ...getNativeElementColor(),
          ".MuiInput-underline:after": {
            borderBottom: "none !important"
          },
          ".MuiInput-underline:before": {
            borderBottom: "none !important"
          },
          "input[type='time']": {
            transition: "all 0.3s",
            maxWidth: "105px",
            px: "16px",
            border: "1px solid",
            borderColor: "outline",
            borderRadius: "32px"
          },
          "input[type='time']:focus": {
            border: "1px solid",
            borderColor: "primary"
          }
        }}
      >
        <Flex mb="22px" alignItems="center">
          <Subtitle mr="16px">{t("scheduler.time.start")}</Subtitle>
          <TextField
            type="time"
            value={startDateObj.format("HH:mm", { hour12: false })}
            onChange={({ target }) => updateTime(target.value, "startTime")}
          />
        </Flex>
        <Flex alignItems="center">
          <Subtitle mr="16px">{t("scheduler.time.stop")}</Subtitle>
          <TextField
            type="time"
            value={endDateObj.format("HH:mm", { hour12: false })}
            onChange={({ target }) => updateTime(target.value, "endTime")}
          />
          {isOverNight && (
            <Subtitle ml="16px">
              {t("scheduler.nextDay")?.split("}}")?.[1]}
            </Subtitle>
          )}
        </Flex>
      </Box>
      <H3 mb="16px">{t("scheduler.repeat")}</H3>
      <Flex alignItems="center" gap="8px">
        {localizedWeekDays.map((day, i) => {
          const dayKey = scheduler.weekDays[i];
          const selected = task.days.includes(dayKey);
          return (
            <Flex
              key={day}
              onClick={() => toggleDay(dayKey)}
              justifyContent="center"
              alignItems="center"
              bg={selected ? "primary" : "transparent"}
              width="40px"
              height="40px"
              borderRadius="100%"
              {...(selected && { border: "1px solid", borderColor: "outline" })}
            >
              <Subtitle color={selected ? "white" : "mediumEmphasis"}>
                {day}
              </Subtitle>
            </Flex>
          );
        })}
      </Flex>
      <Subtitle mt="12px" color="mediumEmphasis">
        {daysAdjective}
      </Subtitle>
    </Box>
  );
}
